import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy
} from '@angular/core';
import { AuthModule } from '../../auth.module';
import {
  Subject,
  filter,
  finalize,
  map,
  mergeMap,
  switchMap,
  take,
  takeUntil,
  tap
} from 'rxjs';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { LoadListener } from '@veone/ngx-utilities/rxjs';
import { Router } from '@angular/router';
import { AuthenticationService } from '~app/app/~shared/services/authentication.service';
import { AuthFacade } from '~app/app/~store/facades/auth.facade';
import { GlobalFacade } from '~app/app/~store/facades/global.facade';
import { ChangePasswordParams, User } from '~core/models/auth.models';
import { removeNilProperties } from '~app/app/~shared/utils/utils';

@Component({
  selector: 'password-init',
  standalone: true,
  imports: [AuthModule],
  templateUrl: './password-init.component.html',
  styleUrls: ['./password-init.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PasswordInitComponent implements OnDestroy {
  hide = true;
  isLoading = false;

  otpCode = '';
  passwordControl = new FormControl<string | null>(null, [
    Validators.required,
    Validators.minLength(8)
  ]);

  form = new FormGroup({
    password: new FormControl<string>('', {
      validators: [Validators.required, Validators.minLength(8)]
    }),
    newPassword: new FormControl<string>('', {
      validators: [Validators.required, Validators.minLength(8)]
    })
  });

  violations$ = this.globalFacade.formViolations$.pipe(
    filter(Boolean),
    tap({
      next: violation => {
        this.passwordControl.setErrors({
          httpError: violation[0].title
        });
      }
    })
  );

  areParamsAvailable$ = this.globalFacade.routeData$.pipe(
    map(data => {
      const { otp, username } = data?.queryParams || {};
      if (!otp || !username) {
        return false;
      }
      return true;
    })
  );

  listener: LoadListener<User | undefined> | undefined;
  private _destroy$ = new Subject<void>();
  constructor(
    private globalFacade: GlobalFacade,
    private authFacade: AuthFacade,
    private authService: AuthenticationService,
    private router: Router,
    private changeDetection: ChangeDetectorRef
  ) {}

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  setPassword(): void {
    this.isLoading = true;
    this.authFacade.currentUser$
      .pipe(
        filter(Boolean),
        take(1),
        switchMap(user => {
          const changeParams = removeNilProperties(
            this.form.value
          ) as ChangePasswordParams;
          return this.authService.changePassword({
            userId: user.id,
            ...changeParams
          });
        }),
        mergeMap(() =>
          this.authFacade.patchLoggedUser({ passwordChanged: true })
        ),
        takeUntil(this._destroy$),
        finalize(() => {
          this.isLoading = false;
          this.changeDetection.detectChanges();
        }),
        tap({
          next: () => this.router.navigate(['/'])
        })
      )
      .subscribe();
  }

  logout(): void {
    this.authFacade.logout();
  }
}
